import { startVoiceWaveAnimation, stopVoiceWaveAnimation } from './animations';
import { endVoiceSessionAPI, endVoiceSessionBeacon, startVoiceSessionAPI } from './api';
import { getConversationId, startConversation, stopConversation } from './eleven-labs';

const SESSION_KEY = 'witzo-voice-active-session';

let microphoneStream: MediaStream | null = null;
let currentApiUrl: string | null = null;
let currentUniqueId: string | null = null;
let unloadListenerRegistered = false;

// ── Session persistence (survives page refresh/close) ──

interface PersistedSession {
  apiUrl: string;
  uniqueId: string;
  conversationId: string | null;
}

function persistSession(apiUrl: string, uniqueId: string, conversationId: string | null) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ apiUrl, uniqueId, conversationId }));
  } catch {
    /* sessionStorage may be unavailable */
  }
}

function clearPersistedSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {}
}

function getPersistedSession(): PersistedSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.apiUrl && data.uniqueId) return data;
    return null;
  } catch {
    return null;
  }
}

// ── Page unload handler (sendBeacon) ──

function handlePageUnload() {
  // Try to get live conversation ID first, fall back to persisted
  const liveConvId = getConversationId();
  const session = getPersistedSession();
  if (!session) return;

  const conversationId = liveConvId || session.conversationId;
  if (conversationId) {
    endVoiceSessionBeacon(session.apiUrl, session.uniqueId, conversationId);
  }
  clearPersistedSession();
}

function registerUnloadHandler() {
  if (unloadListenerRegistered) return;
  window.addEventListener('pagehide', handlePageUnload);
  window.addEventListener('beforeunload', handlePageUnload);
  unloadListenerRegistered = true;
}

function unregisterUnloadHandler() {
  window.removeEventListener('pagehide', handlePageUnload);
  window.removeEventListener('beforeunload', handlePageUnload);
  unloadListenerRegistered = false;
}

// ── Orphaned session recovery ──
// Call this on page load to report any session that wasn't cleanly ended.

export async function recoverOrphanedSession(): Promise<void> {
  const session = getPersistedSession();
  if (!session || !session.conversationId) {
    clearPersistedSession();
    return;
  }

  // Small delay so backend has time to process ElevenLabs data
  await new Promise((resolve) => setTimeout(resolve, 3000));
  const result = await endVoiceSessionAPI(session.apiUrl, session.uniqueId, session.conversationId);
  if (result?.success) {
    clearPersistedSession();
  }
}

// ── Microphone ──

export async function getMicrophoneStream(): Promise<MediaStream> {
  if (microphoneStream) return microphoneStream;
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    throw new Error('Microphone not available. Page must be served over HTTPS or localhost.');
  }
  try {
    microphoneStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    return microphoneStream;
  } catch (err) {
    console.error('Microphone access denied or failed:', err);
    throw err;
  }
}

export function stopMicrophoneStream() {
  if (microphoneStream) {
    microphoneStream.getTracks().forEach((track) => track.stop());
    microphoneStream = null;
  }
}

// ── Voice session lifecycle ──

/**
 * Start conversation and voice wave animation.
 * Routes through the backend to get a signed URL (instead of using agentId directly).
 */
export async function startVoiceSession(
  apiUrl: string,
  uniqueId: string,
  statusElement: HTMLParagraphElement,
  wavesRoot: HTMLDivElement,
  wavelets: HTMLDivElement[],
  messagesContainer: HTMLDivElement,
  micButton: HTMLButtonElement,
  onCareerIntent?: () => void | Promise<void>
): Promise<void> {
  try {
    // Ensure secure context (HTTPS or localhost) — required for microphone access
    if (!window.isSecureContext) {
      throw new Error(
        'Voice requires a secure context (HTTPS or localhost). Please serve the page over HTTPS or http://localhost.'
      );
    }

    // Store for use when ending the session
    currentApiUrl = apiUrl;
    currentUniqueId = uniqueId;

    // Persist early (without conversationId) so beforeunload has something
    persistSession(apiUrl, uniqueId, null);
    registerUnloadHandler();

    // 1. Request signed URL from backend (checks credits + generates URL)
    statusElement.textContent = 'Connecting...';
    const { signedUrl } = await startVoiceSessionAPI(apiUrl, uniqueId);

    // 2. Start conversation with signed URL (SDK handles its own mic access)
    await startConversation(null, signedUrl, statusElement, messagesContainer, micButton, onCareerIntent);

    // 3. Now conversation ID should be available — persist it
    const conversationId = getConversationId();
    persistSession(apiUrl, uniqueId, conversationId);

    // 4. Optional: get mic stream for visualizer (non-blocking)
    if (wavesRoot && wavelets.length > 0) {
      try {
        const stream = await getMicrophoneStream();
        await startVoiceWaveAnimation(stream, wavesRoot, wavelets);
        wavesRoot.classList.remove('hidden');
      } catch (vizErr) {
        console.warn('[witzo-voice] Visualizer mic failed (non-critical):', vizErr);
      }
    }
  } catch (err) {
    currentApiUrl = null;
    currentUniqueId = null;
    clearPersistedSession();
    unregisterUnloadHandler();
    throw err;
  }
}

/**
 * Stop conversation, voice wave animation, and mic stream.
 * Reports to backend for credit deduction.
 */
export async function stopVoiceSession(
  statusElement: HTMLParagraphElement,
  wavesRoot: HTMLDivElement | null
): Promise<void> {
  // Get conversation ID before stopping
  const conversationId = getConversationId();

  // Save API info before clearing
  const apiUrl = currentApiUrl;
  const uniqueId = currentUniqueId;
  currentApiUrl = null;
  currentUniqueId = null;

  // Prevent unload from double-reporting while this explicit stop is in progress
  unregisterUnloadHandler();

  // Clean up UI and streams (errors here must not block credit deduction)
  try {
    stopVoiceWaveAnimation();
    await stopConversation();
    stopMicrophoneStream();
    if (statusElement) statusElement.textContent = 'Ready to listen';
    if (wavesRoot) wavesRoot.classList.add('hidden');
  } catch (uiErr) {
    console.error('[witzo-voice] Error during UI cleanup:', uiErr);
  }

  // Report to backend for credit deduction (always attempt this)
  if (conversationId && apiUrl && uniqueId) {
    // Small delay to allow ElevenLabs to finish processing the conversation
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const result = await endVoiceSessionAPI(apiUrl, uniqueId, conversationId);
    if (result?.success) {
      clearPersistedSession();
    } else {
      persistSession(apiUrl, uniqueId, conversationId);
    }
  } else {
    console.warn('[witzo-voice] Cannot report session end - missing data:', {
      conversationId,
      apiUrl: !!apiUrl,
      uniqueId: !!uniqueId,
    });
  }
}

/**
 * Immediately disconnects audio (ElevenLabs + mic) then fires credit deduction
 * in the background. Use in back/close handlers so navigation is not blocked
 * by the 2-second delay and backend round-trip.
 */
export async function disconnectVoiceNow(
  statusElement: HTMLParagraphElement,
  wavesRoot: HTMLDivElement | null
): Promise<void> {
  const conversationId = getConversationId();
  const apiUrl = currentApiUrl;
  const uniqueId = currentUniqueId;
  currentApiUrl = null;
  currentUniqueId = null;
  unregisterUnloadHandler();

  try {
    stopVoiceWaveAnimation();
    await stopConversation();
    stopMicrophoneStream();
    if (statusElement) statusElement.textContent = 'Ready to listen';
    if (wavesRoot) wavesRoot.classList.add('hidden');
  } catch (uiErr) {
    console.error('[witzo-voice] Error during disconnect:', uiErr);
  }

  // Fire credit deduction in background — orphan recovery handles failures on next load
  if (conversationId && apiUrl && uniqueId) {
    void (async () => {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const result = await endVoiceSessionAPI(apiUrl, uniqueId, conversationId);
      if (result?.success) {
        clearPersistedSession();
      } else {
        persistSession(apiUrl, uniqueId, conversationId);
      }
    })();
  } else {
    clearPersistedSession();
  }
}
